/**
 * Datadog RUM (Real User Monitoring) Client-Side Initialization
 *
 * Provides automatic page view tracking for SPA navigation via History API,
 * and distributed tracing correlation with APM backend traces.
 *
 * Features:
 * - Automatic view tracking on route changes (History API)
 * - RUM-APM correlation via allowedTracingUrls
 * - Privacy-first configuration (mask-user-input by default)
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/
 *
 * @example
 * ```typescript
 * import { initializeDatadogRum } from './lib/rum/datadogRum';
 *
 * // Initialize RUM (client-side only)
 * initializeDatadogRum(config);
 * ```
 */

import { datadogRum } from '@datadog/browser-rum';
import type { RumErrorEvent, RumEvent } from '@datadog/browser-rum';
import { getRumFeatureErrorMessage, REDACTED_RUM_FEATURES } from './rumFeatures';
import { setRumClient } from './rumClient';

// Matches ONLY the browser's opaque cross-origin error placeholders — `Script error.`,
// `Script error`, or the wrapped `Uncaught "Script error."` (optional trailing period /
// surrounding quotes, case-insensitive). Anchored with ^…$ so a real first-party error
// whose message merely *contains* "script error" (e.g. `TypeError: script error handler
// is not a function`) is NOT matched.
const OPAQUE_CROSS_ORIGIN_SCRIPT_ERROR = /^(?:uncaught\s+["']?)?script error\.?["']?$/i;
const MAX_REPORTED_PAGE_FAILURE_VIEWS = 1000;

const isCrossOriginScriptError = (event: RumErrorEvent): boolean => {
    // A cross-origin script that throws without CORS surfaces as an opaque, stackless
    // "Script error." (the browser hides the detail).
    //
    // IAFE-2627: after this change, our OWN scripts are loaded with crossorigin
    // (webpack `output.crossOriginLoading: 'anonymous'` + crossorigin on the SSR bootstrap
    // tags) and our CDN returns `Access-Control-Allow-Origin: *`, so their errors now
    // de-opaque into real messages/stacks and do NOT match here — they flow into RUM so we
    // can see and fix them. What remains opaque is only what we can't control CORS on:
    // browser extensions and third-party tags. Dropping that residue keeps unactionable,
    // un-attributable, non-blocking noise out of the RUM-based Frontend Availability SLO.
    //
    // NOTE (interim): this is a message denylist. Prefer shrinking the residue at the
    // source (get GlobalNav / 3P scripts to send CORS) and/or fixing the SLI definition so
    // extension noise is excluded structurally, rather than relying on this match forever.
    // (Original bug: the previous exact-equality check `=== 'script error.'` missed the
    // `Uncaught "Script error."` form — ~97% of RUM errors — so nothing was ever filtered.)
    const message = event.error?.message?.trim() ?? '';
    return OPAQUE_CROSS_ORIGIN_SCRIPT_ERROR.test(message);
};

/**
 * Marks one original feature error per failed view for the availability SLO.
 */
const reportPageFailureOnce = (event: RumEvent, pageFailureReportedViews: Set<string>): void => {
    // Runtime events can be incomplete even though the SDK type declares view.
    const viewId = (event as { view?: { id?: string } }).view?.id;
    if (!viewId || pageFailureReportedViews.has(viewId)) {
        return;
    }

    const eventContext = event.context;
    if (eventContext?.pageFailure !== true) {
        return;
    }

    // Mark the original event instead of adding a new error. A new error would attach to whichever
    // view is active now, which may differ from this event's view after delayed processing.
    eventContext.pageSuccess = false;

    // Keep recent IDs bounded without clearing on navigation because delayed events can reference
    // old views.
    pageFailureReportedViews.add(viewId);
    if (pageFailureReportedViews.size > MAX_REPORTED_PAGE_FAILURE_VIEWS) {
        const oldestViewId = pageFailureReportedViews.values().next().value;
        if (oldestViewId) {
            pageFailureReportedViews.delete(oldestViewId);
        }
    }
};

/**
 * Feature errors retain their original type and stack frames for Datadog grouping, while the
 * potentially sensitive source message is replaced immediately before transmission.
 */
const redactFeatureErrorMessage = (event: RumErrorEvent): void => {
    const context = event.context;
    const feature = typeof context?.feature === 'string' ? context.feature : undefined;
    if (!feature || !REDACTED_RUM_FEATURES.has(feature)) {
        return;
    }

    const safeMessage = getRumFeatureErrorMessage({
        feature,
        page: context?.page,
        action: context?.action,
        operation: context?.operation,
        failureKind: context?.failureKind,
    });
    const sourceMessage = event.error.message;

    event.error.message = safeMessage;
    if (event.error.stack) {
        event.error.stack = sourceMessage
            ? event.error.stack.replace(sourceMessage, safeMessage)
            : event.error.stack;
    }
};

const createDatadogBeforeSend = (): ((event: RumEvent) => boolean) => {
    const pageFailureReportedViews = new Set<string>();

    return (event: RumEvent): boolean => {
        if (event.type !== 'error') {
            return true;
        }

        if (isCrossOriginScriptError(event)) {
            return false;
        }

        try {
            redactFeatureErrorMessage(event);
            reportPageFailureOnce(event, pageFailureReportedViews);
        } catch (error) {
            console.error('[DatadogRUM] Error at page failure aggregation', error);
        }

        return true;
    };
};

/**
 * Configuration for Datadog RUM initialization
 */
export interface DatadogRumConfig {
    /** Datadog RUM Application ID */
    applicationId: string;
    /** Datadog client token (public, safe for browser) */
    clientToken: string;
    /** Datadog site (e.g., 'datadoghq.com', 'datadoghq.eu') */
    site: string;
    /** Service name (must match APM service name for correlation) */
    service: string;
    /** Environment name (must match APM env for correlation) */
    env: string;
    /** Application version */
    version: string;
    /** GraphQL endpoint URL for APM trace correlation */
    graphqlEndpoint?: string;
}

/**
 * Initializes Datadog RUM for client-side monitoring
 *
 * This function should only be called from client-side code (e.g., App.client.tsx).
 * It automatically tracks:
 * - Page views via History API (pushState, replaceState, popstate)
 * - User interactions (clicks, etc.)
 * - Resources and long tasks
 * - Errors
 *
 * For RUM-APM correlation, it injects Datadog trace headers into
 * requests matching allowedTracingUrls.
 */
export const initializeDatadogRum = (config: DatadogRumConfig): void => {
    // Validate required configuration
    if (!config.applicationId || !config.clientToken) {
        console.warn('[DatadogRUM] Skipping initialization: missing applicationId or clientToken');
        return;
    }

    try {
        datadogRum.init({
            // Core identification
            applicationId: config.applicationId,
            clientToken: config.clientToken,
            site: config.site || 'datadoghq.com',

            // Service identification (MUST match APM service name for correlation)
            service: config.service,
            env: config.env,
            version: config.version,

            // Sampling Configuration
            sessionSampleRate: 100,
            sessionReplaySampleRate: 0,

            // Feature Tracking
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,

            // Privacy Configuration
            defaultPrivacyLevel: 'mask-user-input',

            // RUM-APM Correlation (Distributed Tracing)
            allowedTracingUrls: config.graphqlEndpoint
                ? [
                      {
                          match: config.graphqlEndpoint,
                          propagatorTypes: ['tracecontext', 'datadog'] as const,
                      },
                  ]
                : [],
            traceSampleRate: 100,

            // Filter opaque third-party errors and aggregate feature-owned page failures.
            beforeSend: createDatadogBeforeSend(),
        });
        setRumClient(datadogRum);

        console.log('[DatadogRUM] Initialized successfully', {
            applicationId: config.applicationId.substring(0, 8) + '...',
            env: config.env,
            service: config.service,
        });
    } catch (error) {
        console.error('[DatadogRUM] Failed to initialize:', error);
    }
};
